import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import getWatchLabels from '@/features/home-videos/utils/getWatchLabels';
import { videoShape } from './videoShape';
import useCardWatch from './useCardWatch';
import DurationBadge from './DurationBadge';
import { PlayTile } from './PlayIcons';
import { WatchTextButton } from './TextLinks';

/**
 * Compact row card (the mockup's "icon" Video Card): play tile, title,
 * description, duration and a "Watch" link.
 * Mobile: tile | text, duration under the text, Watch bottom-right.
 * md+:    tile | text | duration (top) and Watch (bottom).
 */
VideoRowCard.propTypes = {
  video: videoShape.isRequired,
  onWatch: PropTypes.func.isRequired
};

export default function VideoRowCard(props) {
  const { video, onWatch } = props;
  const { t } = useTranslation();
  const { watchRef, onCardClick } = useCardWatch(video, onWatch);
  const { duration, watchLabel } = getWatchLabels(video, t);

  return (
    <div
      data-testid="video-card-row"
      onClick={onCardClick}
      className="w-full bg-white border border-cs-gray-100 rounded-[15px] p-2.5 cursor-pointer hover:shadow-md transition-shadow duration-200 grid grid-cols-[72px_minmax(0,1fr)] md:grid-cols-[72px_minmax(0,1fr)_auto] gap-x-2"
    >
      <div className="col-start-1 row-start-1 md:row-span-2 self-start">
        <PlayTile />
      </div>

      <div className="col-start-2 row-start-1 md:row-span-2 flex flex-col gap-3 pt-2 min-w-0 text-cs-gray-700">
        <h3 className="font-semibold text-[17px] leading-6">{video.title}</h3>
        {video.description && (
          <p className="!mb-0 font-normal text-[15px] leading-[1.2]">{video.description}</p>
        )}
      </div>

      {duration && (
        <div className="col-start-2 row-start-2 mt-3 md:col-start-3 md:row-start-1 md:mt-0 self-start justify-self-start md:justify-self-end">
          <DurationBadge label={duration} />
        </div>
      )}

      <div
        ref={watchRef}
        className="col-span-2 row-start-3 md:col-span-1 md:col-start-3 md:row-start-2 self-end justify-self-end"
      >
        <WatchTextButton label={t('home_videos.card.watch')} ariaLabel={watchLabel} />
      </div>
    </div>
  );
}
